import { type LabelComponentType, getLabelComponent } from "../../../../common-deprecated/components/label/Label";
import {
    type UseLabelType,
    type UseOptionalLabelType,
    type GetLabelType,
    type GetOptionalLabelType,
    getUseLabel,
    getUseOptionalLabel,
    getLabelFn,
    getOptionalLabelFn,
} from "../../../../common-deprecated/hooks/useLabel";
import { T1Labels } from "../../../../common-deprecated/utils/t1labels";
import type { BuildAndBuyStateType } from "../redux/store";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { BnbSettingsType } from "../redux/reducers/BnbSettingsReducer";
import { NavStep } from "../../shared-logic/utils/Navigation";
import { TradeInNotificationTypeEnum } from "../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

// These labels are used with the AEM version of the build
export const AEMBuildAndBuyLabels = {
    selectGrade: "Select grade",
    close: "Close",
    // Lexus
    priceFrom: "From",
    customizeButton: "customize",
    addToCompare: "Add to compare",
    spinTopView: "Rooftop",
    selectSubmodel: "Select submodel",
};

// ----------------------------------------------------------------------
// Print PDF (Tridion & AEM)
// ----------------------------------------------------------------------
export const printLabels = {
    printBasePrice: "Base price",
    printOptionalExtras: "Optional extras",
    printTradeInPrice: "Part exchange value",
    printTradeInPriceValid: "Offer valid until {date}",
    printTotalOtrPrice: "Total OTR Price",
    printYourSummary: "Your Summary",
    printColourAndWheels: "Colours & wheels",
    printTaxAndCostsRegion: "Region {region}",
    printPacks: "Packs",
    printExtras: "Extras",
    printEcoSpecs: "Eco specifications",
    printTopSpecs: "Top specifications",
    printLegalInfo: "",
    printInteriorFabricTrim: "Interior Fabric and Trim",
    printYourCode: "Your code",
    printStandardEquipment: "Standard equipment",
    printYourServicePlans: "Additional services",
    printTotalPrice: "total price",
    printCashPrice: "Cash price",
    printSeeRepresentativeExample: "", // See representative example below
    printFinanceTableTitle: "", // Your finance details
    printTaxAndCostsDisclaimer: "",
    printPriceBreakdownTitle: "Price breakdown",
};

export type BuildAndBuyPDFLabelType = keyof typeof printLabels;

// ----------------------------------------------------------------------
// Grade-Engine Step
// ----------------------------------------------------------------------
export const stepGradeEngineLabels = {
    showInterior: "Show interior",
    selectEngine: "Select your engine",
    options: "{value} options",
};

const a11yLabels = {
    bnbA11yNavBuild: "Build navigation menu",
    bnbA11yNavBuildDescription:
        "The user may add/change configurations, and if no change is made, a proposal for a standard model will be given.",
    bnbA11yNavButtonBack: "Go to previous step",
    bnbA11yNavStep: "Step",
    bnbA11yButtonBasketOpen: "Open basket",
    bnbA11yButtonBasketClose: "Close basket",
    bnbA11yButtonPackSelect: "Select pack",
    bnbA11yButtonPackDeselect: "Deselect pack",
    bnbA11yButtonExtraSelect: "Select extra",
    bnbA11yButtonExtraDeselect: "Deselect extra",
    bnbA11yButtonRemoveFromBasket: "Remove from basket",
    bnbA11yButtonMoreInfo: "More info",
    bnbA11yButtonSearchRemove: "Remove search term",
    bnbA11yButtonSearch: "Search",
    bnbA11yButtonSliderNext: "Show next item",
    bnbA11yButtonSliderPrevious: "Show Previous item",
    bnbA11yButtonModalConfigOpen: "Open code modal",
    bnbA11yButtonCo2SpecsDisclaimerOpen: "Open co2 specs disclaimer",
    bnbA11yButtonInsuranceRateClear: "Clear insurance rate",
};

export const buildAndBuyLabels = {
    ...T1Labels,
    ...AEMBuildAndBuyLabels,
    ...printLabels,
    ...a11yLabels,
    ...stepGradeEngineLabels,

    // ----------------------------------------------------------------------
    // Model and Grade steps
    // ----------------------------------------------------------------------
    chooseYourGrade: "Choose your {model}",
    chooseYourEngine: "Choose your engine",
    chooseThisEngine: "Choose this engine",
    thisIsYourEngine: "", // This is your engine (keep empty to allow fallback chooseYourEngine to work),
    thisIsYourEngineButton: "", // This is your engine (keep empty to allow fallback chooseThisEngine to work)
    engineDetails: "Engine details",
    showMore: "Show more",
    showLess: "Show less",
    standardEquipment: "Standard equipment",
    showStandardEquipment: "Show standard equipment",
    selectEquipmentItem: "Select {equipment}",
    deselectEquipmentItem: "Deselect {equipment}",
    modelSpecificEngineDisclaimer: "", // This disclaimer should never be configured in i18n, and should only be set when overriden by ace.

    // proace specific labels on model step
    viewDimensions: "View dimensions", // for Proace modal, used in the grade explorer and the build
    dimensions: "Dimensions", // proace model title

    // specification titles for engine specs
    wltpSpecificationTitle: "", // WLTP specifications
    nedcSpecificationTitle: "", // NEDC specifications
    correlatedNedcSpecificationTitle: "", // Correlated NEDC specifications
    selectGradeAndBodytype: "Select grade",

    // ----------------------------------------------------------------------
    // Customize step
    // ----------------------------------------------------------------------
    mostPopularTitle: "Most popular",
    mostPopularSubTitle: "Most popular {model} extras",
    mostPopularIntroText: "Discover our most popular {model} extras across all categories.",
    colour: "Colours",
    wheels: "Wheels",
    interior: "Interior",
    interiorTopView: "", // "Interior and roof" Lexus only label, used for the interior tab when "view top" = convertible is available
    nextGenInteriorDisclaimer: "", // "The interior image presented on this page should be used for reference purpose only and it is valid solely for the Premiere Edition."

    // Spinner labels
    spinDragToRotate: "Drag to rotate",
    spinLoading: "Loading", // also as fallback for prices
    spinFullscreen: "Fullscreen",
    spinFullscreenClose: "Close",
    spinTopViewToggleButton: "Rooftop {status}", // Lexus only label
    spinTopViewOpen: "open", // Lexus only label
    spinTopViewClosed: "closed", // Lexus only label
    // WebGl 3D Labels
    spin3DView: "3D View",
    spin2DView: "2D View",
    spin3DLoading: "Loading",
    spin3DReset: "Reset",
    spin3DError: "Error loading 3D model",

    // extras labels on customize
    extras: "Extra's",
    showAllExtras: "Show all extra's",
    allExtras: "All extras",
    allExtrasLead: "Browse and add some extras to your car...",
    allExtrasDisclaimer: "",
    searchExtras: "Search extras",
    extrasCount: "{count} extras",
    noExtras: "No extra's available",
    noExtrasFound: "Please try using more general names, check your spelling or use filters.",
    showAllExteriorExtras: "", // Show all exterior extra's
    showAllInteriorExtras: "", // Show all interior extra's
    setItemTooltipDescription: "", // This set consists of
    includesTooltipDescription: "", // This item includes also
    includedPack: "", // Included
    disabledPack: "", // Unavailable
    colourIncludedInPrice: "", // Included in price colour

    // extras filter
    extrasFilterFilter: "Filter",
    extrasFilterCategory: "Category",
    noExtrasFoundHeader: "Sorry, no extras found",
    extrasFilterSearchAmong: "Search among",
    extrasFilterApply: "Apply",
    allExtrasDropdown: "All extras",
    extrasFilterAvailability: "Availability",
    extrasFilterOnlyShowItemsAvailableOnline: "Only show items available online",
    searchAllExtras: "Search extras",

    // packs on customize step
    packsRecommended: "Recommended Packs",
    packsRecommendedDisclaimer: "",
    packsShowFeatures: "{count} extras", // Used In both the build and buy and The packs component
    packsOptional: "Optional equipment",
    optionalPacks: "Optional packs",
    showAllPacks: "Show all packs", // Used in packs component as well
    packConflictTitle: "Conflicts found in pack",
    packConflictContinue: "Continue",
    extraIsIncludedInSelectedPack: "Included in selected pack",
    extraIsAvailableInPack: "Also available within pack",
    extraIsOnlyAvailableInPack: "Only available within a pack",

    // packs notifications
    extrasRemovedNotificationTitle: "Extra's removed",
    extrasRemovedNotificationDescription:
        "One of your selected extras was removed because it was part of a pack you selected.",
    optionalPackExtrasRemovedNotificationTitle: "Optional pack extra's removed",
    optionalPackExtrasRemovedNotificationDescription:
        "One of your selected optional extras was removed because it was part of a pack you deselected.",

    // extra only available inside packs modals (single pack or multiple packs)
    extraOnlyPackSingleModalTitle: "Only available within a pack",
    extraOnlyPackSingleModalConfirmAddPack: "Add pack",
    extraOnlyPackSingleModalCancel: "Cancel",
    extraOnlyPackMultipleModalTitle: "Only available within {count} packs",
    extraOnlyPackMultipleModalClose: "Close",

    // conflict modals
    equipmentConflictTitle: "Equipment conflict",
    serviceProductConflictTitle: "Service Product conflict",
    serviceProductConflictContinue: "Continue",

    // Label used in the personalization and basket
    notAvailableOnlineBasketWarning: "", // optional, "This colour and interior combination is not available online, only available at your retailer."

    // winterwheels
    winterWheelsHeader: "Winter wheels set",
    winterWheelsOptional: "Winter set (optional)",
    winterWheelsStartingFrom: "Starting from",
    winterWheelsChooseButton: "Choose winter set",
    winterWheelsEditButton: "Edit winter set",
    winterWheelsRemoveButton: "Remove set",
    winterWheelsModalTitle: "Winter wheels",
    winterWheelsModalDescription: "Choose your winter wheels and tyres",
    winterWheelsModalSoftCompound: "Soft compound",
    winterWheelsModalSoftCompoundEuropean: "Soft compound European",
    winterWheelsModalSoftCompoundNordic: "Soft compound Nordic",
    winterWheelsModalStudded: "Studded",
    winterWheelsModalAddSetButton: "Add this set",
    winterWheelsModalAddedButton: "Added",
    winterWheelsModalConfirmSetButton: "Confirm wheels selection",
    winterWheelsModalCancelSetButton: "Cancel",
    winterWheelsModalInstallationTitle: "Installation",
    winterWheelsModalTotalWinterSetPriceTitle: "Total cost",
    winterWheelsModalPartNumber: "P/N:",
    winterWheelsEuLabelFuelEfficiency: "Fuel Efficiency",
    winterWheelsEuLabelWetGrip: "Wet Grip",
    winterWheelsEuLabelNoise: "Noise",
    winterWheelsModalInstallationDescription: "Would you like to have your winter wheels installed at the dealer?",
    winterWheelsModalInstallationYesText: "Yes, and I accept that the local dealer rates will be charged.",
    winterWheelsModalInstallationYesTextWithPrice: "Yes at the additional cost of {cost}.",
    winterWheelsModalInstallationNoText: "No thanks",
    winterWheelsModalInstallationTyreHotel:
        "If you'd like to store your tyres in a tyre hotel, please contact your local dealer for a quote.",
    winterWheelsModalExcludingInstallationCosts: "(excluding installation costs)",
    winterWheelsModalIncludingInstallationCosts: "(including installation costs)",
    winterWheelsModalTyreSize: "Tyre size:",
    winterWheelsToggleButtonWinterSet: "Winter set",

    // Finance Toggle Modal
    financeToggleModalDescription: "",
    financeToggleModalCancel: "Cancel",

    // ----------------------------------------------------------------------
    // Summary step
    // ----------------------------------------------------------------------
    techSpecs: "Technical specifications",
    yourCustomisation: "Your customisation",
    colourAndWheels: "Colours & wheels", // unused in v2
    interiorTrim: "Interior trim",
    yourCode: "Your code",
    yourCodeStep1Title: "Scan QR code",
    yourCodeStep2Title: "Your customisation",
    yourCodeStep1Description:
        "Scan the code with your smartphone by using your phone’s camera. You will be directed to our form.",
    yourCodeStep2Description: "View your customisation link of your selected car with the best offer.",
    ecoDescription: "", // also used in the lexus print
    ecoDisclaimer: "", // also used in the lexus print
    changeCustomisation: "Change customisation",
    changeGrade: "Change model",
    total: "Total",
    savingConfig: "Saving configuration...",
    errorSavingConfig: "Something went wrong saving the configuration.",
    disabledLoader: "The data is being loaded, please wait until completion.", // shown when navigating to summary via buy step (SPA)
    passiveSummaryChangeModalTitle: "Want to change your model?",
    passiveSummaryWarning: "Warning: your promotion might no longer be valid on your new configuration.",
    passiveSummaryEditConfiguration: "Edit configuration",
    passiveSummaryCancelEditConfiguration: "Cancel",

    // eta
    deliveryMonth: "Delivery {month} {year}",
    deliveryWeeks: "Delivery from {weeks} weeks",
    deliverySingleWeek: "Delivery this week",
    earlierAvailableCars: "Earlier available cars",
    etaDisclaimer: "", // leave empty so the tooltip is not shown if there is no label.
    deliveryExceededNumberOfWeeks: "",
    alternatives: "Cars with earlier availability",
    viewStockCarsDescription: "",
    viewStockCars: "",
    noDelivery: "",
    chooseThisGrade: "Choose this model",

    // Costs and taxes
    costsAndTaxes: "Costs and taxes",
    costsAndTaxesDescription:
        "Immatruculation tax is calculated based on the region you live in. Please select your region to calculate the exact cost.",

    // ----------------------------------------------------------------------
    // Buy step
    // ----------------------------------------------------------------------
    buyBackToSummary: "Back to summary",

    // Buy cash labels
    buyThisCar: "Buy this car",
    buyTitle: "Buying a car from the comfort of your home.",
    buyDescription: "",
    buyStepTitle0: "",
    buyStepTitle1: "",
    buyStepTitle2: "",
    buyStepTitle3: "",
    buyStepTitle4: "",
    buyStepTitle5: "",
    buyBuyNow: "", // Should be "Buy this car now", but was made empty by default so the button could be hidden
    buyWatchHowToBuy: "Watch how to buy",
    buyTermsConditionsLink: "Terms & conditions",
    buyTermsConditionsTitle: "Terms and conditions",
    buyTermsConditionsDescription: "",
    buyTermsConditionsScroll1: "",
    buyTermsConditionsScroll2: "",
    buyTermsConditionsExtra: "",
    buyTermsConditionsOk: "Ok",

    // Buy monthly labels
    buyThisCarMonthly: "",
    buyTitleMonthly: "",
    buyDescriptionMonthly: "",
    buyStepTitle0Monthly: "",
    buyStepTitle1Monthly: "",
    buyStepTitle2Monthly: "",
    buyStepTitle3Monthly: "",
    buyStepTitle4Monthly: "",
    buyStepTitle5Monthly: "",
    buyBuyNowMonthly: "",
    buyTermsConditionsLinkMonthly: "",
    buyTermsConditionsTitleMonthly: "",
    buyTermsConditionsDescriptionMonthly: "",
    buyTermsConditionsScroll1Monthly: "",
    buyTermsConditionsScroll2Monthly: "",
    buyTermsConditionsExtraMonthly: "",
    buyTermsConditionsOkMonthly: "",

    // ----------------------------------------------------------------------
    // Reservation step
    // ----------------------------------------------------------------------

    reserveBackToSummary: "Back to summary",

    // Reserve cash labels
    reserveThisCar: "Reserve this car",
    reserveTitle: "Reserving a car from the comfort of your home.",
    reserveDescription: "",
    reserveStepTitle0: "",
    reserveStepTitle1: "",
    reserveStepTitle2: "",
    reserveStepTitle3: "",
    reserveStepTitle4: "",
    reserveStepTitle5: "",
    reserveReserveNow: "", // Should be "Reserve this car now", but was made empty by default so the button could be hidden
    reserveTermsConditionsLink: "Terms & conditions",
    reserveTermsConditionsTitle: "Terms and conditions",
    reserveTermsConditionsDescription: "",
    reserveTermsConditionsScroll1: "",
    reserveTermsConditionsScroll2: "",
    reserveTermsConditionsExtra: "",
    reserveTermsConditionsOk: "Ok",

    // Reserve monthly labels
    reserveThisCarMonthly: "",
    reserveTitleMonthly: "",
    reserveDescriptionMonthly: "",
    reserveStepTitle0Monthly: "",
    reserveStepTitle1Monthly: "",
    reserveStepTitle2Monthly: "",
    reserveStepTitle3Monthly: "",
    reserveStepTitle4Monthly: "",
    reserveStepTitle5Monthly: "",
    reserveReserveNowMonthly: "",
    reserveTermsConditionsLinkMonthly: "",
    reserveTermsConditionsTitleMonthly: "",
    reserveTermsConditionsDescriptionMonthly: "",
    reserveTermsConditionsScroll1Monthly: "",
    reserveTermsConditionsScroll2Monthly: "",
    reserveTermsConditionsExtraMonthly: "",
    reserveTermsConditionsOkMonthly: "",

    // ----------------------------------------------------------------------
    // Lease step
    // ----------------------------------------------------------------------

    leaseBackToSummary: "Back to summary",

    // Lease cash labels
    leaseThisCar: "Lease this car",
    leaseTitle: "Leasing a car from the comfort of your home.",
    leaseDescription: "",
    leaseStepTitle0: "",
    leaseStepTitle1: "",
    leaseStepTitle2: "",
    leaseStepTitle3: "",
    leaseStepTitle4: "",
    leaseStepTitle5: "",
    leaseLeaseNow: "", // Should be "Lease this car now", but was made empty by default so the button could be hidden
    leaseTermsConditionsLink: "Terms & conditions",
    leaseTermsConditionsTitle: "Terms and conditions",
    leaseTermsConditionsDescription: "",
    leaseTermsConditionsScroll1: "",
    leaseTermsConditionsScroll2: "",
    leaseTermsConditionsExtra: "",
    leaseTermsConditionsOk: "Ok",

    // Lease monthly labels
    leaseThisCarMonthly: "",
    leaseTitleMonthly: "",
    leaseDescriptionMonthly: "",
    leaseStepTitle0Monthly: "",
    leaseStepTitle1Monthly: "",
    leaseStepTitle2Monthly: "",
    leaseStepTitle3Monthly: "",
    leaseStepTitle4Monthly: "",
    leaseStepTitle5Monthly: "",
    leaseLeaseNowMonthly: "",
    leaseTermsConditionsLinkMonthly: "",
    leaseTermsConditionsTitleMonthly: "",
    leaseTermsConditionsDescriptionMonthly: "",
    leaseTermsConditionsScroll1Monthly: "",
    leaseTermsConditionsScroll2Monthly: "",
    leaseTermsConditionsExtraMonthly: "",
    leaseTermsConditionsOkMonthly: "",

    // ----------------------------------------------------------------------
    // Basket
    // ----------------------------------------------------------------------

    basket: "Basket", // used as title in mobile basket on customize
    basketOverview: "Overview", // used as title in mobile basket on summary
    basketTotal: "Total",
    basketTotalMonthlyCosts: "",
    basketYourModel: "Your {name}", // used in old basket + buy step
    basketRetailPrice: "Retail price",
    basketSummaryAndSave: "Summary and save",
    basketSummaryAndSaveMonthly: "", // "Continue with finance", made empty to allow the fallback to the cash label
    basketToServices: "Go to services", // for v2
    basketExtras: "Extras",
    basketPacks: "Packs",
    basketFinancePlan: "Finance plan", // used in lexus basket and as fallback in v2 for finance service title
    basketTitleSummaryCta: "", // "Would you like to", replacement for basketYourModel
    basketTotalTitle: "", // "Overview"
    basketServicePlans: "Services",
    yourServicePlans: "Your service plans",
    basketInsuranceTitle: "Insurance",
    basketInsuranceDescription: "Get a personalized insurance quote",
    basketTotalValueBeforeTax: "Total value before tax",

    // labels related to the switch banner below the basket
    financeOptionQuestionCash: "Consider buying with cash?", // "Consider buying with cash?"
    financeOptionQuestionMonthly: "Considering Finance?", // "Considering Finance?"
    financeOptionSwitchCash: "", // "Switch to cash"
    financeOptionSwitchMonthly: "", // "Switch to finance"

    // basket ctas
    saveToYourAccount: "Save to your account", // cta at bottom of summary
    addInsurance: "Add insurance", // lexus only label for insurance cta in summary basket

    // Build labels for tradein buttons
    partExchange: "Get a part exchange offer",
    partExchangeTitle: "Part exchange",
    editPartExchange: "Edit part exchange",
    viewPartExchange: "View part exchange",
    removePartExhange: "Remove",

    // Build labels for insurance plans
    removeInsurance: "Remove",

    // Build labels for service plans
    addServicePlans: "Add to deal",
    removeServicePlans: "Remove",
    exclusiveOnlineServicePlans: "Online exclusive",
    nonFinanceableServicePlans: "Pay upfront",
    nonFinanceableBasketTotal: "Total to pay upfront",
    servicesTermsAndConditions: "View terms and conditions",

    // Build labels for finance packs
    financePackIncluded: "included",

    // finance toggles overrides
    financeOptionTitleCash: "", // "Cash"
    financeOptionTitleMonthly: "", // "Finance"

    // total price panel
    selectedFinanceOptionTitle: "", // "Selected payment", only visible when translated
    selectedFinanceOptionCash: "Cash", // instead of financeOptionCash with an actual correct value for "cash"
    selectedFinanceOptionMonthly: "Finance",
    financeOptionTotalCash: "", // "Cash payment"
    financeOptionTotalMonthly: "", // "Finance payment"
    paymentMethod: "Payment method",

    // extra toggle panels
    financeOptionShortSwitchCash: "", // "View cash price >"
    financeOptionShortSwitchMonthly: "", // "View monthly finance price >"
    mobileFinanceOptionTitle: "", // "Payment"
    mobileFinanceOptionDescription: "", // "Select your payment option"

    // Integrated trade-in
    tradeinBasketUpdate: "Part Exchange Update",
    tradeinBasketTooHighCash: "Your car valuation is higher than the total value of the car",
    tradeinBasketTooHighMonthly: "Your car valuation is too high and can not be used with a monthly finance plan",

    // Integrated trade-in validation scenarios.
    // Half of these are currently unused (errors are now hidden) but keeping them here to maintain an overview.
    // Explanation of scenarios: https://cde.toyota-europe.com/confluence/display/OR/Finance+scenarios
    tradeinBasketAdded: "We have successfully added your part exchange to your finance quote", // Scenario 1
    tradeinBasketReducedDeposit:
        "We have successfully added your part exchange to the finance deposit but reduced your cash deposit", // Scenario 2
    tradeinBasketRefused:
        "We can not use your part exchange in the finance deposit because it exceeds the maximum allowed deposit", // Scenario 3
    tradeinBasketReducedDepositCarValue: "We reduced your cash deposit because the total value of the car decreased", // Scenario 5A
    tradeinBasketRefusedCarValue:
        "We removed your part exchange from the deposit because the total value of the car decreased and the part exchange is now over the maximum deposit", // Scenario 5B
    tradeinBasketRefusedCarValueDeposit:
        "We removed your part exchange from the deposit because the total value of the car decreased and the part exchange is over the maximum deposit. We also increased your cash deposit to match the finance settings.", // Scenario 5C
    tradeinBasketAddedCarValue:
        "We added your part exchange to the finance deposit because the total value of the car increased and the part exchange value is now below the maximum allowed deposit", // Scenario 6
    tradeinBasketAddedCash: "We have subtracted your part exchange value from the total car price",
    tradeinBasketRefusedCash: "Your part exchange cannot be used as it has a higher value than the total car price",
    tradeInBasketNotSupported: "Part exchange is not available in PCH leasing",
    tradeInBasketRefusedFinanceProduct:
        "Your part exchange valuation cannot be used with PCH. Switch to Cash or PCP to use your part exchange valuation.  Or continue with PCH and discuss selling your part exchange with your nearest Toyota Centre.",

    // ----------------------------------------------------------------------
    // Navigation labels
    // ----------------------------------------------------------------------

    // top bar and title references
    stepModelAndEngine: "Model and engine",
    stepCustomise: "Customise",
    stepServices: "Services",
    stepSummaryAndSave: "Summary and save",
    stepBuy: "Buy", // Deprecated in Tridion
    checkoutStep: "", // "Buy" / "Reserve" / "Buy or Reserve"
    // Only used for lexus
    stepGrade: "Grade",

    // bottom nav
    stepBackToModelEngine: "", // "Back to Model & Engine"
    stepBackToGrade: "Back to Grade", // Back to Grade
    stepBackToCustomize: "", // "Back to Customization"
    stepBackToServices: "", // "Back to Services"

    navigationStepOf: "Step {current} of {max}", // Re-introduced label

    // ----------------------------------------------------------------------
    // Disclaimers
    // collection of labels used all around but all to do with disclaimers and legal stuff
    // ----------------------------------------------------------------------

    disclaimers: "Disclaimers", // Title, label is also used in lexus print pdf
    checkoutDisclaimer: "",
    checkoutDisclaimerMonthly: "",
    "T1.disclaimerNedc": "",
    "T1.disclaimerCorrelatedNedc": "",
    "T1.disclaimerWltp": "",
    "T1.genericTotalMonthlyRateDisclaimer": "",
    "T1.genericTotalPriceDisclaimer": "",
    genericDisclaimer: "", // The generic disclaimer is by default empty, if it is filled in it is shown in the disclaimer section of online retail.
    genericFinanceDisclaimer: "", // This is a generic finance disclaimer which is only shown on the customize and summary steps this disclaimer only shows up when monthly rate is active (= toggled). Specifically implemented for DK/DA
    accessoryDisclaimer: "", // Used in the build and the seperate equipment and packs component
    printDisclaimer: "", // The printDisclaimer label should be dynamically referenced from the corresponding disclaimer used in the print page (this is also used for the Lexus print PDF).
    overlayDisclaimer: "",
    priceFootNote: "", // priceFootNote is empty bylabel="overview" default, if it is filled in it is shown under the total price.
    priceFootNoteExcludingVat: "",
    disclaimer: "Disclaimer", // used as monthly rates version of priceFootNote
    quotes: "Quotes", // finance quotes title
    mobileFooterDisclaimerMonthly: "",

    // WLTP
    wltpTotalPriceDisclaimer: "", // Cash total The information provided on this site is an estimate. Always check the exact emission level and the total car tax with your Toyota dealer
    wltpTotalMonthlyDisclaimer: "", // Monthly total The information provided on this site is an estimate. Always check the exact emission level and the total car tax with your Toyota dealer
    wltpTotalErrorDisclaimer:
        "Extras selected for the car can have an impact on CO2 emissions and thus on the car tax. The final emission values depend on the combined effect of the equipment and the information provided on this site is an estimate. Always check the exact emission level and the total car tax with your Toyota dealer",
    co2Value: "CO2 {value}",
    co2Disclaimer:
        "Some optional equipment may lead to an increase of CO2, hence have an impact on tax and a total cost of the car",
    wltpTotalPriceBeforeTax: "", // Leave this empty, if it is not provided by the labels service, it is not shown
    wltpTaxTitle: "", // "First year Charges" If this field and wltpTax is emtpy, the tax section won't be shown.
    wltpTax: "", // "Tax" If empty, the tax section won't be shown.
    // The next two labels are used for commercial vehicles, aka vehicles excl. VAT. These labels are optional and only used when filled in and the model is configured to show prices excl. VAT.
    wltpTaxTitleCommercial: "",
    wltpTaxCommercial: "",

    // ----------------------------------------------------------------------
    // Varia
    // ----------------------------------------------------------------------

    // services step labels
    servicesIntroduction: "Services",
    servicesDescription: "", // optional
    noServices: "No services available",

    // generic labels
    standard: "Standard", // used to denote a standard colour, wheel or upholstery
    optional: "Optional", // used in lexus items spin as fallback category
    ok: "Ok", // used in generic content modal as close button
    moreInfo: "More info",
    financeOptionCash: "",
    financeOptionMonthly: "Monthly plan",

    // price related labels
    monthlyTerm: "{term} months",
    monthlyTermTitle: "Duration",
    monthlyAnnualInterestRate: "{rate}% annual interest rate",
    monthlyEffectiveInterestRate: "{rate}% effective interest rate",
    promoFromUntilDate: "Valid from {from} until {until}",
    promoFromDate: "Valid from {from}",
    promoUntilDate: "Valid until {until}",
    priceLoading: "", // "Loading", empty so the spinLoading label can be used as fallback

    // Labels for the code injected finance/insurance steps
    financeButton: "Personalise finance quote",
    financeButtonCash: "", // "Finance calculator", empty to avoid untranslated label from showing. Unused in toyota build
    financeModalTitle: "Your financial monthly plan",
    insuranceTitle: "Insurance",
    insuranceInPageTitle: "Insurance",
    insuranceInPageText:
        "You’ve got the car, now get the cover. Insure your Toyota with us and enjoy one of the most comprehensive levels of care available.",
    insuranceInPageDetails: "Why do I need insurance",
    insuranceInPageDetailsLabel: "Why do I need insurance",
    insuranceCollapsableCta: "Show details",
    insuranceCollapsableTitle: "Toyota insurance",
    insuranceNotificationTitle: "Insurance Added",
    insuranceNotificationDescription: "Your insurance pack has been added to your basket.",
    financeButtonEdit: "",
    financeInPageTitle: "Finance",
    financeInPageText:
        "With Financial Lease from Toyota Financial Services you have a tailor-made financing method. Get a finance plan on your specific financial options.",
    financeInPageDetails: "Discover all the benefits",
    financeInPageDetailsLabel: "Discover all the benefits",
    financeCollapsableCta: "Show details",
    financeCollapsableTitle: "Finance your car",
    financeNotificationTitle: "Finance has been updated",
    financeNotificationDescription: "Your finance details have been updated with the selected items.",
    insuranceText:
        "You’ve got the car, now get the cover. Insure your Toyota with us and enjoy one of the most comprehensive levels of care available.",
    insuranceButton: "Personalise insurance quote",
    insuranceButtonEdit: "Edit insurance quote",
    insuranceModalTitle: "Your insurance monthly plan",

    // Mass management related labels
    massManagementBasketNotification: "", // Extras exceed permitted weight by {value} kg. You can easily change your selection [button]here[/button].
    massManagementPopupNotification: "Before continuing you'll need to comply with the maximum allowed mass.",
    massManagementModalTitle: "Mass management",
    massManagementModalDescription:
        "You have exceeded the max weight by {value} kg through pack/extra selection. Please remove the ones to comply with the maximum weight.",
    massManagementModalSelectedItemsTitle: "Selected extras with mass impact",
    massManagementModalSelectedItemsValidates:
        "You removed enough accessories to comply, you can now save and close the modal to continue.",
    massManagementModalSelectedItemsError: "Deselect extras to decrease the weight by {value} kg.",
    massManagementModalOk: "Save and change",
    massManagementModalCancel: "Cancel",
    massManagementErrorMessage: "",

    // error messages
    tyCodeErrorMessage: 'Could not load the code "{tyCode}"',

    // Tyre labels
    tyreInfo: "Tyre info",
    winterTyreInfo: "Tyre info for winter wheels",
    tyreInfoDescription: "",
    tyreInfoFallback: "Detail page",
    tyreInfoFallbackDescription: "This image could not be loaded. To see more information go to:",
    tyreInfoNoEprelFallback: "", // For further information please contact your local retailer.

    // No accessories tax labels
    genericDisclaimerTitle: "",
    genericModelDisclaimer: "",
    genericEngineDisclaimer: "",
    genericTyreDisclaimer: "",
    genericBasketDisclaimer: "",
    genericPdfDisclaimer: "",

    // Generic price disclaimers
    genericPriceDisclaimerBuy: "",
    genericPriceDisclaimerCustomize: "",
    genericPriceDisclaimerGrades: "",
    genericPriceDisclaimerReserve: "",
    genericPriceDisclaimerSubmodel: "",
    genericPriceDisclaimerSummary: "",

    // Tax disclaimers used in the basket and disclaimer footers when available.
    basketTaxDisclaimerTitle: "",
    basketTaxDisclaimerContent: "",

    // PDF specific disclaimers
    pdfInsuranceDisclaimer: "",

    // ----------------------------------------------------------------------
    // Labels of deprecated components
    // ----------------------------------------------------------------------

    // labels only used in carexplorer (incomplete!)
    matchesFound: "{count} matches found",
    noMatchesFound: "No matches found",
    showMatches: "Show matches",

    // TODO: cleanup unused labels

    // Labels only used in deprecated engine / model compare
    // comparePanelEnginesHeader: "Select two or three engines above to compare", // CompareV1Disclaimer
    compareAdd: "Add to compare", // also used in build
    // compareCancel: "Cancel",
    // compareSectionEngines: "Engines",
    // compareSectionEquipment: "Equipment",
    // compareSectionSpecs: "Technical specs",
    // compareSectionPacks: "Packs",
    // comparePanelHeader: "Select two or three models above to compare",
    // comparePanelButtonView: "View comparison",
    // comparePanelButtonHide: "Hide comparison",
    // compareDownloadPdf: "Download PDF",
    // compareModelsTitle: "Compare models",
    // compareModelsDescription: "Compare the standard equipment on your car, optional items can be added later.",
    // compareEnginesTitle: "Compare Engines",
    // compareEnginesDescription: "",
    // compareAddModel: "Add car",
    // compareAddEngine: "Add engine",
    // compareChange: "Change",
    // compareBuild: "Build",
    // compareStandard: "Standard",
    // compareOptional: "Optional",
    // compareNotApplicable: "Not applicable",
    // comparePromotions: "Promotions",
    // compareExceptions: "Exceptions",
    // compareShowAllEquipment: "Show all equipment",
    // compareShowDifferencesOnly: "Show differences only",
    // compareChangeModel: "Change car",
    // compareChangeEngine: "Change engine",
    // The following 4 labels are used in the add car modal overlay in the compare
    // compareSelectCar: "Select a model",
    // compareSelectModel: "Select a grade",
    // compareSelectBodytype: "Select a bodytype",
    // compareSelectEngine: "Select an engine",
    // compareSectionGrades: "Grades",
    // comparePanelHeaderSmall: "Select two models above to compare",
    // comparePanelEnginesHeaderSmall: "Select two engines above to compare",
    // pdfDisclaimerTitle: "",
    // pdfDisclaimerContent: "",
    engineCashDisclaimer: "",

    // yourModel: "Your model", // unused
    // ctaTitle: "Happy with your {model}?", // unused
    // ctaText: "You’ve built your model, selected the colour and seen all the options and accessories available.", // unused
    // ctaViewSummary: "View summary", // unused
    // noEtaFound: "No ETA found", // unused
    // loadingEta: "Loading ETA", // unused in build, should be added to compareV2 labels?
    // deliveryEmptyResponse: "", // unused
    // packsShowFeature: "Show feature", // unused
    // packsHideFeatures: "Hide {count} features", // unused
    // packsHideFeature: "Hide feature", // unused
    // packsMorePersonalization: "More personalizations", // unused
    // summarySpecificationTitle: "Specifications", // unused
    // summaryEngineTitle: "Engine", // unused
    // closeMobileBasket: "Return", // unused
    // buyThisCarModel: "Buy your {model} {grade}", // unused
    // buyThisCarModelMonthly: "", // unused
    // basketChangeYourEngine: "Change your engine", // unused
    // buySelectYourDealer: "", // unused
    // buyStepDescription1: "", // unused
    // buyStepDescriptions2: "", // unused
    // buyStepDescription3: "", // unused
    // buyStepDescription4: "", // unused
    // buyStepDescription5: "", // unused
    // basketViewCar: "View car", // unused
    // basketPackExtras: "Pack extras", // unused
    // yourGrade: "Your Model", // unused
    // engines: "Engines", // unused
    // contactDealer: "Contact dealer", // unused
    // cashFrom: "", // unused
    // monthlyFrom: "", // unused
    // getOffer: "Get an instant online offer", // unused
    // stepOf: "{current} of {max}", // unused
    // stepsClose: "Close", // unused
};

export const nextLabels: Partial<Record<NavStep, BuildAndBuyLabelType>> = {
    [NavStep.Services]: "basketToServices",
    [NavStep.Summary]: "basketSummaryAndSave",
    [NavStep.Buy]: "buyThisCar",
};

export const bnbIntegratedTradeInNotificationsLabels: Record<TradeInNotificationTypeEnum, BuildAndBuyLabelType> = {
    [TradeInNotificationTypeEnum.Added]: "tradeinBasketAdded",
    [TradeInNotificationTypeEnum.ReducedDeposit]: "tradeinBasketReducedDeposit",
    [TradeInNotificationTypeEnum.Refused]: "tradeinBasketRefused",
    [TradeInNotificationTypeEnum.AddedCarValue]: "tradeinBasketAddedCarValue",
    [TradeInNotificationTypeEnum.ReducedDepositCarValue]: "tradeinBasketReducedDepositCarValue",
    [TradeInNotificationTypeEnum.RefusedCarValue]: "tradeinBasketRefusedCarValue",
    [TradeInNotificationTypeEnum.RefusedCarValueDeposit]: "tradeinBasketRefusedCarValueDeposit",
    [TradeInNotificationTypeEnum.AddedCash]: "tradeinBasketAddedCash",
    [TradeInNotificationTypeEnum.RefusedCash]: "tradeinBasketRefusedCash",
    [TradeInNotificationTypeEnum.NotSupported]: "tradeInBasketNotSupported",
};

export type BuildAndBuyLabelType = keyof typeof buildAndBuyLabels;
type BuildAndBuyLabelParamType = Partial<
    Record<
        | "cost"
        | "count"
        | "from"
        | "model"
        | "submodel"
        | "month"
        | "name"
        | "rate"
        | "term"
        | "until"
        | "value"
        | "weeks"
        | "year"
        | "tyCode"
        | "date"
        | "current"
        | "max"
        | "equipment"
        | "region",
        string | number
    >
>;

type BnbLabelStateType = { commonSettings: CommonSettingsType; bnbSettings: BnbSettingsType };
export const useBnbLabel = getUseLabel(
    (state: BuildAndBuyStateType) => state.bnbSettings.labels,
    (state: BuildAndBuyStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseLabelType<BuildAndBuyLabelType, BuildAndBuyLabelParamType>;
export const useBnbOptionalLabel = getUseOptionalLabel(
    (state: BuildAndBuyStateType) => state.bnbSettings.labels,
    (state: BuildAndBuyStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseOptionalLabelType<BuildAndBuyLabelType, BuildAndBuyLabelParamType>;
export const getBnbLabel = getLabelFn(
    (state: BnbLabelStateType) => state.bnbSettings.labels,
    (state: BnbLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetLabelType<BnbLabelStateType, BuildAndBuyLabelType, BuildAndBuyLabelParamType>;
export const getBnbOptionalLabel = getOptionalLabelFn(
    (state: BnbLabelStateType) => state.bnbSettings.labels,
    (state: BnbLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetOptionalLabelType<BnbLabelStateType, BuildAndBuyLabelType, BuildAndBuyLabelParamType>;
export const BnbLabel = getLabelComponent(useBnbLabel) as LabelComponentType<
    BuildAndBuyLabelType,
    BuildAndBuyLabelParamType
>;
